import { createSlice } from "@reduxjs/toolkit";
import {
  getAcquirerList,
  getIsoList,
  getMerchantList,
} from "./AdminFields.thunk";

const baseState = {
  error: null,
  acquirerId: "",
  acquirerCurrencyId: "",
  isoID: "",
  merchantId: "",
  applyChanges: false,
  wasUsed: false,
  reset: false,
  // Acquirer
  acquirerError: null,
  acquirerStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  acquirerData: [],
  acquirerPage: 1,
  acquirerPageSize: 5,
  acquirerTotalPages: 0,
  acquirerTotalRecords: 0,
  // ISO
  isoError: null,
  isoStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  isoData: [],
  isoPage: 1,
  isoPageSize: 5,
  isoTotalPages: 0,
  isoTotalRecords: 0,
  // Merchant
  merchantError: null,
  merchantStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  merchantData: [],
  merchantPage: 1,
  merchantPageSize: 5,
  merchantTotalPages: 0,
  merchantTotalRecords: 0,
  merchantReqhashMAC: null,
};

const initialState = {
  ...baseState,
};

export const adminFieldSlice = createSlice({
  name: "adminField",
  initialState,
  reducers: {
    // Reset actions
    resetStore: () => {
      return { ...baseState };
    },
    setAcquirerId: (state, action) => {
      state.acquirerId = action.payload;
    },
    setIsoID: (state, action) => {
      state.isoID = action.payload;
    },
    setAcquirerCurrencyId: (state, action) => {
      state.acquirerCurrencyId = action.payload;
    },
    setMerchantId: (state, action) => {
      state.merchantId = action.payload;
    },
    setApplyChanges: (state, action) => {
      state.applyChanges = action.payload;
    },
    setWasUsed: (state, action) => {
      state.wasUsed = action.payload;
    },
    setReset: (state, action) => {
      state.reset = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAcquirerList.pending, (state) => {
        state.acquirerStatus = "loading";
        state.acquirerError = null;
      })
      .addCase(getAcquirerList.fulfilled, (state, action) => {
        const { acquirerData, page, page_size, total_pages, total_records } =
          action.payload;
        state.acquirerStatus = "succeeded";
        state.acquirerData = acquirerData;
        state.acquirerPage = page;
        state.acquirerPageSize = page_size;
        state.acquirerTotalPages = total_pages;
        state.acquirerTotalRecords = total_records;
        state.acquirerError = null;
      })
      .addCase(getAcquirerList.rejected, (state, action) => {
        state.acquirerStatus = "failed";
        state.acquirerData = [];
        state.acquirerPage = 1;
        state.acquirerPageSize = 5;
        state.acquirerTotalPages = 0;
        state.acquirerTotalRecords = 0;
        state.acquirerError =
          action?.payload?.message || "Error fetching acquirer list";
      });
    builder
      .addCase(getIsoList.pending, (state) => {
        state.isoStatus = "loading";
        state.isoError = null;
      })
      .addCase(getIsoList.fulfilled, (state, action) => {
        const { data, page, page_size, total_pages, total_records } =
          action.payload;
        state.isoStatus = "succeeded";
        state.isoData = data;
        state.isoPage = page;
        state.isoPageSize = page_size;
        state.isoTotalPages = total_pages;
        state.isoTotalRecords = total_records;
        state.isoError = null;
      })
      .addCase(getIsoList.rejected, (state, action) => {
        state.isoStatus = "failed";
        state.isoData = [];
        state.isoPage = 1;
        state.isoPageSize = 5;
        state.isoTotalPages = 0;
        state.isoTotalRecords = 0;
        state.isoError =
          action?.payload?.message || "Error fetching iso enrollment list";
      });
    builder
      .addCase(getMerchantList.pending, (state) => {
        state.merchantStatus = "loading";
        state.merchantError = null;
      })
      .addCase(getMerchantList.fulfilled, (state, action) => {
        const { merchantList, reqhashMAC, page, page_size, total_records } =
          action.payload;
        state.merchantData = merchantList;
        state.merchantReqhashMAC = reqhashMAC;
        // state.merchantID = null;
        // state.merchantName = null;
        // state.merchantStore = null;
        // state.terminalList = [];
        state.merchantStatus = "succeeded";
        state.merchantError = null;
        state.merchantPage = page;
        state.merchantPageSize = page_size;
        state.merchantTotalRecords = total_records;
      })
      .addCase(getMerchantList.rejected, (state, action) => {
        state.merchantStatus = "failed";
        state.merchantError =
          action.payload?.message || "Error getting merchant list";
        state.merchantPage = 1;
        state.pageSize = 5;
        state.merchantTotalRecords = 0;
        state.merchantData = [];
      });
  },
});

export const {
  resetStore,
  acquirerId,
  acquirerCurrencyId,
  setAcquirerId,
  setAcquirerCurrencyId,
  setIsoID,
  setMerchantId,
  setApplyChanges,
  setWasUsed,
  setReset,
  reset,
  acquirerStatus,
  acquirerData,
  acquirerPage,
  acquirerPageSize,
  acquirerTotalPages,
  acquirerTotalRecords,
  acquirerError,
  isoStatus,
  isoData,
  isoPage,
  isoPageSize,
  isoTotalPages,
  isoTotalRecords,
  isoError,
  merchantStatus,
  merchantData,
  merchantPage,
  merchantPageSize,
  merchantTotalPages,
  merchantTotalRecords,
  merchantError,
} = adminFieldSlice.actions;

export default adminFieldSlice.reducer;
